import { useClientScope, type ApiScope } from "@/features/utils/api-scope";
import { useCurrentClient } from "./use-clients";

/**
 * The client a screen is about, and the mount of the API that answers for it.
 *
 * Inside a {@link ClientScopeProvider} (the client's file in the platform
 * back-office) that is the provider's; everywhere else it is the current
 * client, resolved exactly as the tenant app always has (`useCurrentClient`) —
 * so a screen moved onto this hook behaves the same in the tenant app.
 */
export function useScopedClient(): { clientId: string; scope: ApiScope } {
  const scoped = useClientScope();
  const current = useCurrentClient()?.client?.id || "";
  return scoped
    ? { clientId: scoped.clientId, scope: scoped.scope }
    : { clientId: current, scope: "tenant" };
}

/**
 * Where a screen's links go, for the screens mounted in both places.
 *
 * In the tenant app, the client's own pages. On the client's file in the
 * platform back-office, its tabs — and `null` for a page that only exists in
 * the tenant app (a contact's file), which the screen then draws without a
 * link rather than sending staff to a page of their own tenant.
 */
export function useClientLinks(): ClientLinks {
  const { clientId, scope } = useScopedClient();

  if (scope === "staff") {
    const file = `/admin/clients/${clientId}`;
    return {
      calls: () => `${file}/calls`,
      call: (sessionId) => `${file}/calls/session/${sessionId}`,
      contacts: () => null,
      contact: () => null,
    };
  }

  return {
    calls: () => `/${clientId}/calls`,
    call: (sessionId) => `/${clientId}/calls/session/${sessionId}`,
    contacts: () => `/${clientId}/contacts`,
    contact: (customerId) => `/${clientId}/contacts/${customerId}`,
  };
}

type ClientLinks = {
  calls: () => string;
  call: (sessionId: string) => string;
  contacts: () => string | null;
  contact: (customerId: string) => string | null;
};
